import {computed, MaybeRefOrGetter, ref, toValue} from 'vue'
import {SUPPLIER_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'

export type PayNowEntryLine = {
    invoiceNo?: string
    creditAccount?: string
    creditSubAccount?: string
    debitSubAccount?: string
    date: Date
}

export type PayNowBankAccount = {
    bookkeepingAccountNo?: string
    bankUuid?: string
}

export const usePayNow = (
    transactionEntryLines: MaybeRefOrGetter<PayNowEntryLine[]>,
    bankAccounts: MaybeRefOrGetter<PayNowBankAccount[]>,
    defaultOutgoingPaymentAccount: MaybeRefOrGetter<string | undefined>,
    isEditing: MaybeRefOrGetter<boolean>,
) => {

    const innerPayNow = ref(true)

    const anyIncomingInvoice = computed(() => {
        return toValue(transactionEntryLines)
            .some(line => line.invoiceNo && line.creditAccount === SUPPLIER_ACCOUNT_NO && line.creditSubAccount)
    })

    const paymentLines = computed(() => {
        const now = new Date()
        now.setHours(0, 0, 0, 0)

        return toValue(transactionEntryLines).filter(line =>
            line.date >= now
            && line.creditAccount && toValue(bankAccounts).find(ba => ba.bookkeepingAccountNo === line.creditAccount)
            && line.debitSubAccount // TODO check if receivable bank account no?
        )
    })

    const errors = computed(() => {
        const errorList: string[] = []
        const banks = toValue(bankAccounts)

        // Check payment line errors
        paymentLines.value.forEach((line) => {
            const bankAccount = banks.find(b => b.bookkeepingAccountNo === line.creditAccount)
            if (bankAccount && !bankAccount.bankUuid) {
                errorList.push('payNow.missingBankIntegration')
            }
        })

        // Check default account errors for incoming invoices
        if (anyIncomingInvoice.value) {
            const defaultAccount = toValue(defaultOutgoingPaymentAccount)

            if (!defaultAccount) {
                errorList.push('payNow.missingDefaultPaymentAccount')
            } else {
                const bankAccount = banks.find(b => b.bookkeepingAccountNo === defaultAccount)
                if (!bankAccount) {
                    errorList.push('payNow.missingDefaultPaymentAccount')
                } else if (!bankAccount.bankUuid) {
                    errorList.push('payNow.missingBankIntegration')
                }
            }
        }

        return errorList
    })

    const isValidPayment = computed(() => {
        // Valid if we have something to pay and no errors
        const hasPayableItems = paymentLines.value.length > 0 || anyIncomingInvoice.value
        return hasPayableItems && errors.value.length === 0
    })

    const showPayNow = computed(() => {
        // Show if not in edit mode and any payment line or incoming invoice present
        return !toValue(isEditing) && (Boolean(paymentLines.value.length) || anyIncomingInvoice.value)
    })

    const payNow = computed({
        get: () => {
            if (showPayNow.value && isValidPayment.value) {
                return innerPayNow.value
            } else {
                return false
            }
        },
        set: (val: boolean) => {
            if (showPayNow.value && isValidPayment.value) {
                innerPayNow.value = val
            }
        }
    })

    return {
        showPayNow,
        isValidPayment,
        payNow,
        errors,
    }
}